// ** Custom Hooks
import { useQueryWithLocationSearch } from "hooks/useQuery";

// ** Custom Filters
import { isValid as filterLookingAgeIsValid } from "hooks/filters/useFilterAgeSlider";
import {
	isValid as filterSexIsValid,
	isValidCouple as filterSexIsValidCouple,
} from "hooks/filters/useFilterSex";
import { isValid as filterSearchIsValid } from "hooks/filters/useFilterSearchSex";
import {
	isValidInQuery as filterPreferencesQueryIsValid,
	isValidInParam as filterPreferencesParamIsValid,
} from "hooks/filters/useFilterPreferences";
import { isValid as filterAgeIsValid } from "hooks/filters/useFilterAgeRange";
import { isValid as filterContentIsValid } from "hooks/filters/useFilterContent";
import { isValid as filterPurposeIsValid } from "hooks/filters/useFilterPurpose";
import { isValid as filterLanguageIsValid } from "hooks/filters/useFilterLanguage";
import { isValid as filterHeightIsValid } from "hooks/filters/useFilterHeight";
import { isValid as filterFigureIsValid } from "hooks/filters/useFilterFigure";
import { isValid as filterZodiacIsValid } from "hooks/filters/useFilterZodiac";
import { isValid as filterHairIsValid } from "hooks/filters/useFilterHair";
import { isValid as filterRelationshipIsValid } from "hooks/filters/useFilterRelationship";
import { isValid as filterKidsIsValid } from "hooks/filters/useFilterKids";
import { isValid as filterDrinkingIsValid } from "hooks/filters/useFilterDrinking";
import { isValid as filterSmokingIsValid } from "hooks/filters/useFilterSmoking";
import { isValid as filterEducationIsValid } from "hooks/filters/useFilterEducation";
import { isValid as filterDistanceIsValid } from "hooks/filters/compute/useFilterDistanceCompute";
import { isValid as filterProfileNameIsValid } from "hooks/filters/useFilterProfileName";
import useFilterSort from "hooks/filters/useFilterSort";

// #####################################################

const useValidator = ({ preferenceUrl, locationSearch }) => {
	const query = useQueryWithLocationSearch(locationSearch);

	const {
		sort: sortUrl,
		page: pageUrl,
		sex: sexUrl,
		sex_couple: sexCouple1Url,
		sex_couple_partner: sexCouple2Url,
		age_from: ageMinUrl,
		age_to: ageMaxUrl,
		distance: distanceUrl,
		who_search: searchSexUrl,
		purpose: searchForUrl,
		only: onlyUrl,
		who_search_age: searchAgeUrl,
		language_search: languageUrl,
		height_from: heightMinUrl,
		height_to: heightMaxUrl,
		figure_type: figureUrl,
		zodiac: zodiacUrl,
		hair: hairUrl,
		relationship: relationshipUrl,
		kids: kidsUrl,
		drinking: drinkingUrl,
		smoking: smokingUrl,
		education: educationUrl,
		preferences: preferenceQueryUrl,
		nick: nickUrl,
	} = query;

	// ** Filtr - Sort - 'Sortowanie Wyników'
	const { isValid: filterSortIsValid } = useFilterSort();

	const validQueryParamsKeys = [
		"age_from",
		"age_to",
		"distance",
		"drinking",
		"education",
		"figure_type",
		"hair",
		"height_from",
		"height_to",
		"kids",
		"language_search",
		"nick",
		"only",
		"page",
		"preferences",
		"purpose",
		"relationship",
		"sex",
		"sex_couple",
		"sex_couple_partner",
		"smoking",
		"sort",
		"who_search",
		"who_search_age",
		"zodiac",
	];

	const validQueryParams = () => {
		return {
			age_from: filterAgeIsValid(ageMinUrl),
			age_to: filterAgeIsValid(ageMaxUrl),
			distance: filterDistanceIsValid(distanceUrl),
			drinking: filterDrinkingIsValid(drinkingUrl),
			education: filterEducationIsValid(educationUrl),
			figure_type: filterFigureIsValid(figureUrl),
			hair: filterHairIsValid(hairUrl),
			height_from: filterHeightIsValid(heightMinUrl),
			height_to: filterHeightIsValid(heightMaxUrl),
			kids: filterKidsIsValid(kidsUrl),
			language_search: filterLanguageIsValid(languageUrl),
			nick: filterProfileNameIsValid(nickUrl),
			only: filterContentIsValid(onlyUrl),
			page: Number.isInteger(Number(pageUrl)) && Number(pageUrl) > 0,
			preferences: filterPreferencesQueryIsValid(preferenceQueryUrl),
			purpose: filterPurposeIsValid(searchForUrl),
			relationship: filterRelationshipIsValid(relationshipUrl),
			sex: filterSexIsValid(sexUrl),
			sex_couple: filterSexIsValidCouple(sexUrl, sexCouple1Url),
			sex_couple_partner: filterSexIsValidCouple(sexUrl, sexCouple2Url),
			smoking: filterSmokingIsValid(smokingUrl),
			sort: filterSortIsValid(sortUrl),
			who_search: filterSearchIsValid(searchSexUrl),
			who_search_age: filterLookingAgeIsValid(searchAgeUrl),
			zodiac: filterZodiacIsValid(zodiacUrl),
		};
	};

	const actualQueryParamsKeys = Object.keys(query);

	const isIncludeAllQueryParams = () =>
		validQueryParamsKeys.every((element) => {
			return (
				actualQueryParamsKeys.includes(element) &&
				actualQueryParamsKeys.length === validQueryParamsKeys.length
			);
		});
	const containInvalidQueryParam = () => {
		return Object.values(validQueryParams()).includes(false);
	};

	const containInvalidPreferenceParam = () => {
		return !filterPreferencesParamIsValid(preferenceUrl);
	};

	// #####################################################

	return {
		includeAllQueryParams: isIncludeAllQueryParams,
		containInvalidQueryParam,
		containInvalidPreferenceParam,
	};
};

// #####################################################

export default useValidator;

// #####################################################
